import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpDown, ArrowUp, ArrowDown, Download, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import StatusPill from "@/components/StatusPill";
import AnswerLogModal from "@/components/AnswerLogModal";
import FYSelect from "@/components/FYSelect";
import { loadFinancialYears } from "@/lib/fy";

const TODAY = new Date().toISOString().slice(0, 10);

function isOverdue(t) {
  return t.status === "pending" && t.due_date && t.due_date < TODAY;
}

/** Priority bucket for default sort: 0=overdue, 1=pending, 2=completed, 3=cancelled */
function sortPriority(t) {
  if (isOverdue(t)) return 0;
  if (t.status === "pending") return 1;
  if (t.status === "completed") return 2;
  return 3;
}

function SortHeader({ label, sortKey, currentKey, dir, onSort, className = "" }) {
  const active = currentKey === sortKey;
  const Icon = active ? (dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className={`text-left px-5 py-2.5 bc-label cursor-pointer select-none hover:text-[#C41E1E] transition-colors group ${className}`}
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon size={11} className={`${active ? "text-[#C41E1E]" : "text-[#BBBBBB] group-hover:text-[#C41E1E]"} transition-colors`} />
      </span>
    </th>
  );
}

export default function MyTests() {
  const { user } = useAuth();
  const [params] = useSearchParams();

  // ── Data
  const [tests, setTests] = useState([]);
  const [employees, setEmployees] = useState({});
  const [fy, setFy] = useState(undefined);

  // ── Filters
  const [activeOnly, setActiveOnly] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");   // "all"|"TNI"|"Effectiveness"
  const [statusFilter, setStatusFilter] = useState("all"); // "all"|"pending"|"overdue"|"completed"|"cancelled"

  // ── Sort
  const [sortKey, setSortKey] = useState(null);  // null = default
  const [sortDir, setSortDir] = useState("asc");

  // ── Actions
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [activeAnswerLogId, setActiveAnswerLogId] = useState(null);
  const [exporting, setExporting] = useState(false);

  const isEmployee = user.system_role === "employee";
  const isOrgView = !isEmployee; // all non-employee roles see org-level Test Register
  const canManage = user.system_role === "hr" || user.system_role === "super_admin"; // write actions only
  const canCancel = canManage;

  // ── Initialise filters from URL params (from dashboard links)
  useEffect(() => {
    const f = params.get("status");
    const flag = params.get("flag");
    if (f === "overdue") setStatusFilter("overdue");
    else if (f === "pending") setStatusFilter("pending");
    if (flag === "major") { setTypeFilter("Effectiveness"); setStatusFilter("completed"); }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load current FY on mount
  useEffect(() => {
    loadFinancialYears().then((d) => setFy((prev) => (prev === undefined ? d.current_fy : prev)));
  }, []);

  // ── Fetch assessments whenever filters/FY change
  useEffect(() => {
    if (fy === undefined) return;
    const p = {
      ...(isOrgView ? {} : { employee_id: user.employee_id }),
      ...(fy === null ? {} : { fy }),
      ...(isOrgView ? { active_only: activeOnly } : {}),
    };
    api.get("/assessments", { params: p }).then((r) => setTests(r.data));
    if (isOrgView) {
      api.get("/employees", { params: { active_only: false } })
        .then((r) => setEmployees(Object.fromEntries(r.data.map((e) => [e.employee_id, e]))));
    }
  }, [isOrgView, user.employee_id, fy, activeOnly]);

  // ── Derived: filtered + sorted rows
  const rows = useMemo(() => {
    let result = tests.filter((t) => {
      if (typeFilter !== "all" && t.type !== typeFilter) return false;
      if (statusFilter === "all") return true;
      if (statusFilter === "overdue") return isOverdue(t);
      if (statusFilter === "pending") return t.status === "pending" && !isOverdue(t);
      return t.status === statusFilter;
    });

    result = [...result].sort((a, b) => {
      if (sortKey) {
        let va = "", vb = "";
        if (sortKey === "employee") {
          va = employees[a.employee_id]?.name || "";
          vb = employees[b.employee_id]?.name || "";
        } else if (sortKey === "skill") {
          va = a.skill_name || ""; vb = b.skill_name || "";
        } else if (sortKey === "due_date") {
          va = a.due_date || "9999"; vb = b.due_date || "9999";
        } else if (sortKey === "assigned_date") {
          va = a.assigned_date || ""; vb = b.assigned_date || "";
        }
        const cmp = va < vb ? -1 : va > vb ? 1 : 0;
        return sortDir === "asc" ? cmp : -cmp;
      }
      // Default sort: overdue → pending → completed → cancelled
      const pa = sortPriority(a), pb = sortPriority(b);
      if (pa !== pb) return pa - pb;
      if (pa <= 1) return (a.due_date || "9999") < (b.due_date || "9999") ? -1 : 1;
      if (pa === 2) return (a.completed_date || "") > (b.completed_date || "") ? -1 : 1;
      return 0;
    });

    return result;
  }, [tests, typeFilter, statusFilter, sortKey, sortDir, employees]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelling(true);
    try {
      await api.patch(`/assessments/${cancelTarget.assessment_id}/cancel`);
      toast.success("Assessment cancelled");
      setCancelTarget(null);
      const p = {
        ...(isOrgView ? {} : { employee_id: user.employee_id }),
        ...(fy === null ? {} : { fy }),
        ...(isOrgView ? { active_only: activeOnly } : {}),
      };
      const r = await api.get("/assessments", { params: p });
      setTests(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel assessment");
    } finally {
      setCancelling(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const p = {
        ...(isOrgView ? {} : { employee_id: user.employee_id }),
        ...(fy === null ? {} : { fy }),
        ...(isOrgView ? { active_only: activeOnly } : {}),
        ...(typeFilter !== "all" ? { type: typeFilter } : {}),
        // status filter: backend doesn't know "overdue" — export all pending, let xlsx colouring show it
        ...(statusFilter !== "all" && statusFilter !== "overdue" ? { status: statusFilter } : {}),
      };
      const resp = await api.get("/assessments/export", { params: p, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `TestRegister_${TODAY}.xlsx`;
      anchor.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  // ── Counts for filter tabs
  const counts = useMemo(() => ({
    all: tests.length,
    pending: tests.filter((t) => t.status === "pending" && !isOverdue(t)).length,
    overdue: tests.filter(isOverdue).length,
    completed: tests.filter((t) => t.status === "completed").length,
    cancelled: tests.filter((t) => t.status === "cancelled").length,
  }), [tests]);

  const empName = (empId) => employees[empId]?.name || `#${empId}`;

  return (
    <div data-testid="tests-page">
      <PageHeader
        title={isOrgView ? "Test Register" : "My Tests"}
        breadcrumbs={[{ label: "Home", to: "/" }, { label: isOrgView ? "Test Register" : "My Tests" }]}
        action={
          <div className="flex items-center gap-3">
            {canManage && (
              <button
                type="button"
                onClick={() => setActiveOnly(!activeOnly)}
                data-testid="active-only-toggle"
                className={`text-xs font-medium px-3 py-1.5 rounded-sm border transition-colors ${
                  activeOnly
                    ? "bg-[#F9F9F9] border-[#E5E5E5] text-[#666666] hover:border-[#C41E1E] hover:text-[#C41E1E]"
                    : "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100"
                }`}
              >
                {activeOnly ? "Active employees only" : "All employees (incl. resigned)"}
              </button>
            )}
            {canManage && (
              <button
                type="button"
                onClick={handleExport}
                disabled={exporting}
                data-testid="export-excel-btn"
                className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-sm border border-[#E5E5E5] bg-[#F9F9F9] text-[#444] hover:border-[#C41E1E] hover:text-[#C41E1E] transition-colors disabled:opacity-50"
              >
                <Download size={12} />
                {exporting ? "Exporting…" : "Export Excel"}
              </button>
            )}
            <FYSelect value={fy ?? null} onChange={setFy} allowAllTime testid="test-register-fy-select" />
          </div>
        }
      />

      <div className="px-4 md:px-10 py-8 space-y-6">
        {/* Include-resigned banner */}
        {!activeOnly && canManage && (
          <div className="flex items-center gap-3 text-sm bg-amber-50 border border-amber-200 rounded-sm px-4 py-2" data-testid="include-resigned-banner">
            <span className="text-amber-700 font-medium">Showing all employees including resigned / on-leave</span>
            <button type="button" onClick={() => setActiveOnly(true)} data-testid="hide-resigned-btn" className="ml-auto text-xs text-amber-700 font-medium hover:underline">
              Back to active only
            </button>
          </div>
        )}

        {/* Filter bar */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Type pills */}
          <div className="flex items-center gap-1" data-testid="type-filter-group">
            {[
              { v: "all", label: "All types" },
              { v: "TNI", label: "TNI" },
              { v: "Effectiveness", label: "Effectiveness" },
            ].map(({ v, label }) => (
              <button
                key={v}
                type="button"
                data-testid={`type-filter-${v}`}
                onClick={() => setTypeFilter(v)}
                className={`text-xs font-medium px-3 py-1 rounded-sm border transition-colors ${
                  typeFilter === v
                    ? "bg-[#0D0D0D] border-[#0D0D0D] text-white"
                    : "bg-white border-[#E5E5E5] text-[#666] hover:border-[#0D0D0D]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-[#E5E5E5]" />

          {/* Status pills */}
          <div className="flex items-center gap-1 flex-wrap" data-testid="status-filter-group">
            {[
              { v: "all", label: `All (${counts.all})` },
              { v: "overdue", label: `Overdue (${counts.overdue})` },
              { v: "pending", label: `Pending (${counts.pending})` },
              { v: "completed", label: `Completed (${counts.completed})` },
              { v: "cancelled", label: `Cancelled (${counts.cancelled})` },
            ].map(({ v, label }) => (
              <button
                key={v}
                type="button"
                data-testid={`status-filter-${v}`}
                onClick={() => setStatusFilter(v)}
                className={`text-xs font-medium px-3 py-1 rounded-sm border transition-colors ${
                  statusFilter === v
                    ? v === "overdue"
                      ? "bg-red-600 border-red-600 text-white"
                      : "bg-[#0D0D0D] border-[#0D0D0D] text-white"
                    : "bg-white border-[#E5E5E5] text-[#666] hover:border-[#0D0D0D]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {(sortKey || typeFilter !== "all" || statusFilter !== "all") && (
            <button
              type="button"
              onClick={() => { setSortKey(null); setSortDir("asc"); setTypeFilter("all"); setStatusFilter("all"); }}
              className="ml-auto text-xs text-[#999] hover:text-[#C41E1E] transition-colors"
              data-testid="clear-filters-btn"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Main table */}
        <div className="bc-card overflow-x-auto" data-testid="test-register-table">
          <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
            <span className="text-xs text-[#666]">
              Showing <span className="font-semibold text-[#0D0D0D]">{rows.length}</span> of <span className="font-semibold text-[#0D0D0D]">{tests.length}</span> assessments
            </span>
            {sortKey && (
              <button
                type="button"
                onClick={() => { setSortKey(null); setSortDir("asc"); }}
                className="text-xs text-[#999] hover:text-[#C41E1E] transition-colors"
              >
                Reset sort
              </button>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#F9F9F9] border-b border-[#E5E5E5]">
                {isOrgView && (
                  <SortHeader label="Employee" sortKey="employee" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                )}
                <SortHeader label="Skill" sortKey="skill" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <SortHeader label="Assigned" sortKey="assigned_date" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="text-left px-5 py-2.5 bc-label">
                  <span title="TNI = Training Need Identification test. Effectiveness = Post-training assessment." className="cursor-help inline-flex items-center gap-1">
                    Type <span className="text-[#BBBBBB] text-[10px] leading-none select-none">ⓘ</span>
                  </span>
                </th>
                <th className="text-left px-5 py-2.5 bc-label">Required</th>
                <SortHeader label="Due" sortKey="due_date" currentKey={sortKey} dir={sortDir} onSort={handleSort} />
                <th className="text-left px-5 py-2.5 bc-label">Status</th>
                <th className="px-5 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={isOrgView ? 8 : 7} className="px-5 py-10 text-center text-[#999] text-sm">
                    No assessments match the current filters.
                  </td>
                </tr>
              )}
              {rows.map((t) => {
                const overdue = isOverdue(t);
                const empId = t.employee_id;
                const emp = employees[empId];
                const isCompleted = t.status === "completed";
                const isPending = t.status === "pending";

                return (
                  <tr key={t.assessment_id}
                    data-testid={`test-row-${t.assessment_id}`}
                    data-overdue={overdue ? "true" : "false"}
                    className={`border-b border-[#F5F5F5] hover:bg-[#FAFAFA] transition-colors ${
                      overdue ? "bg-red-50/30" : ""
                    }`}
                  >
                    {isOrgView && (
                      <td className="px-5 py-2.5">
                        {emp ? (
                          <Link
                            to={`/competency-card/${empId}`}
                            data-testid={`employee-link-${t.assessment_id}`}
                            className="font-medium text-[#0D0D0D] hover:text-[#C41E1E] hover:underline transition-colors"
                          >
                            {emp.name}
                          </Link>
                        ) : (
                          <span className="text-[#999]">{empName(empId)}</span>
                        )}
                        {emp && emp.status !== "active" && (
                          <span className="ml-2 text-[10px] text-amber-600 font-medium uppercase">{emp.status}</span>
                        )}
                      </td>
                    )}

                    {/* Skill — links to answer log for completed assessments */}
                    <td className="px-5 py-2.5 font-medium">
                      {isCompleted ? (
                        <button
                          type="button"
                          onClick={() => setActiveAnswerLogId(t.assessment_id)}
                          data-testid={`skill-link-${t.assessment_id}`}
                          className="text-left text-[#0D0D0D] hover:text-[#C41E1E] hover:underline transition-colors"
                        >
                          {t.skill_name}
                        </button>
                      ) : (
                        <span data-testid={`cell-skill-${t.assessment_id}`}>{t.skill_name}</span>
                      )}
                    </td>

                    <td className="px-5 py-2.5 text-[#666] tabular-nums">{t.assigned_date || "—"}</td>

                    <td className="px-5 py-2.5">
                      <span data-testid={`cell-type-${t.assessment_id}`} className={`text-xs font-medium px-2 py-0.5 rounded-sm ${
                        t.type === "Effectiveness"
                          ? "bg-purple-50 text-purple-700 border border-purple-200"
                          : "bg-blue-50 text-blue-700 border border-blue-200"
                      }`}>
                        {t.type}
                      </span>
                    </td>

                    <td className="px-5 py-2.5 text-[#666]">L{t.required_level ?? "—"}</td>

                    <td className={`px-5 py-2.5 tabular-nums ${overdue ? "text-[#C41E1E] font-medium" : "text-[#666]"}`}>
                      {t.due_date || "—"}
                    </td>

                    <td className="px-5 py-2.5">
                      {overdue ? (
                        <span title="This test was not completed by its due date" className="cursor-help inline-block">
                          <StatusPill status="overdue" testid={`status-${t.assessment_id}`} />
                        </span>
                      ) : (
                        <StatusPill status={t.status} testid={`status-${t.assessment_id}`} />
                      )}
                    </td>

                    {/* Actions column */}
                    <td className="px-5 py-2.5">
                      <div className="flex items-center gap-2 justify-end">
                        {/* View answers for completed (also accessible via skill name click) */}
                        {isCompleted && (
                          <button
                            type="button"
                            onClick={() => setActiveAnswerLogId(t.assessment_id)}
                            data-testid={`view-answers-${t.assessment_id}`}
                            className="text-xs text-[#666] hover:text-[#C41E1E] hover:underline transition-colors"
                          >
                            View Answers
                          </button>
                        )}
                        {/* Take test — own pending tests, regardless of system_role */}
                        {t.employee_id === user.employee_id && isPending && (
                          <Link
                            to={`/test/${t.assessment_id}`}
                            data-testid={`take-test-${t.assessment_id}`}
                            className="text-xs font-medium text-[#C41E1E] hover:underline transition-colors"
                          >
                            Take Test
                          </Link>
                        )}
                        {/* Cancel — HR/Super Admin, pending tests only */}
                        {canCancel && isPending && (
                          <button
                            type="button"
                            onClick={() => setCancelTarget(t)}
                            data-testid={`cancel-assessment-${t.assessment_id}`}
                            className="flex items-center gap-0.5 text-xs text-[#999] hover:text-[#C41E1E] transition-colors"
                          >
                            <X size={10} />
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Cancel confirmation modal */}
      {cancelTarget && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" data-testid="cancel-confirm-modal">
          <div className="bc-card w-full max-w-md p-6 space-y-4">
            <h3 className="font-heading font-semibold text-lg">Cancel assessment?</h3>
            <p className="text-sm text-[#444]">
              Cancel this assessment for{" "}
              <strong>{empName(cancelTarget.employee_id)}</strong>
              {" — "}
              <strong>{cancelTarget.skill_name}</strong>?{" "}
              This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={() => setCancelTarget(null)}
                disabled={cancelling}
                data-testid="cancel-modal-dismiss"
                className="px-4 py-2 text-sm rounded-sm border border-[#E5E5E5] text-[#444] hover:border-[#999] transition-colors"
              >
                Never mind
              </button>
              <button
                type="button"
                onClick={handleCancelConfirm}
                disabled={cancelling}
                data-testid="cancel-modal-confirm"
                className="px-4 py-2 text-sm rounded-sm bg-[#C41E1E] text-white hover:bg-[#A01818] transition-colors disabled:opacity-50"
              >
                {cancelling ? "Cancelling…" : "Cancel assessment"}
              </button>
            </div>
          </div>
        </div>
      )}

      <AnswerLogModal
        assessmentId={activeAnswerLogId}
        onClose={() => setActiveAnswerLogId(null)}
      />
    </div>
  );
}
